import { Component, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import Lenis from "lenis";
import Intro from "./components/Intro";
import Grain from "./components/Grain";
import Nav from "./components/Nav";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import MediaSection from "./components/MediaSection";
import BetaCTA from "./components/BetaCTA";
import Footer from "./components/Footer";
import { slugToIndex, PHONE_TRACKS } from "./data/curated";

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }
    static getDerivedStateFromError() {
        return { failed: true };
    }
    render() {
        if (this.state.failed) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-void text-sm text-dim">
                    Something broke the current. Refresh to try again.
                </div>
            );
        }
        return this.props.children;
    }
}

function Site() {
    const [introDone, setIntroDone] = useState(false);
    const [activeSection, setActiveSection] = useState("");
    const lenisRef = useRef(null);

    // Own the scroll position: browsers restore the previous session's
    // scroll (mid-page) and Safari defers it until the intro's scroll-lock
    // lifts — landing mid-page on skip. Always launch at the top.
    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        lenisRef.current = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    useEffect(() => {
        if (lenisRef.current) {
            if (introDone) lenisRef.current.start();
            else lenisRef.current.stop();
        }
    }, [introDone]);

    // Deep link (?track=…): glide down to the player on arrival.
    const initialTrackSlug = useRef(
        new URLSearchParams(window.location.search).get("track")
    );
    const deepLinkScrolled = useRef(false);
    useEffect(() => {
        if (deepLinkScrolled.current) return;
        const slug = initialTrackSlug.current;
        const isPhoneTrack = PHONE_TRACKS.some((t) => t.id === slug);
        if (!slug || (slugToIndex(slug) < 0 && !isPhoneTrack)) return;
        deepLinkScrolled.current = true;
        const t = setTimeout(() => {
            const player = document.querySelector('[data-testid="phone-audio-section"]');
            if (!player) return;
            if (lenisRef.current)
                lenisRef.current.scrollTo(player, { offset: -110, duration: 1.8 });
            else
                player.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 600);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const ids = ["experience", "curation", "beta"];
        const compute = () => {
            if (window.scrollY < 100) {
                setActiveSection("");
                return;
            }
            const line = window.innerHeight * 0.4;
            let current = "experience";
            for (const id of ids) {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= line) current = id;
            }
            setActiveSection(current);
        };
        window.addEventListener("scroll", compute, { passive: true });
        const iv = window.setInterval(compute, 200);
        compute();
        return () => {
            window.removeEventListener("scroll", compute);
            window.clearInterval(iv);
        };
    }, []);

    const returnToTop = () => {
        setActiveSection("");
        if (lenisRef.current) lenisRef.current.scrollTo(0, { duration: 1.3 });
        else window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollTo = (id) => {
        setActiveSection(id);
        if (id === "experience" && window.matchMedia("(max-width: 767px)").matches) {
            const phone = document.querySelector('[data-testid="phone-mockup"]');
            if (phone && lenisRef.current) {
                const r = phone.getBoundingClientRect();
                const target = window.scrollY + r.top + r.height / 2 - window.innerHeight / 2;
                lenisRef.current.scrollTo(target, { duration: 1.3 });
                return;
            }
        }
        const el = document.getElementById(id);
        if (!el) return;
        if (lenisRef.current)
            lenisRef.current.scrollTo(el, {
                offset:
                    id === "curation" &&
                    window.matchMedia("(min-width: 768px)").matches
                        ? -46
                        : -70,
                duration: 1.3,
            });
        else el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div
            className="relative min-h-screen bg-void font-sans text-ivory"
            data-testid="rivvr-site"
        >
            <Grain />
            <AnimatePresence>
                {!introDone && (
                    <Intro onComplete={() => setIntroDone(true)} />
                )}
            </AnimatePresence>
            <Nav active={introDone} onNavigate={scrollTo} activeSection={activeSection} onLogoClick={returnToTop} />
            <main>
                <Hero
                    active={introDone}
                    fast={false}
                    onNavigate={scrollTo}
                />
                <Marquee />
                {/* Defer media players until after the intro — having a <video>
                    with an assigned src in the DOM before the intro's play()
                    call preempts the browser's autoplay slot on desktop. */}
                {introDone && <MediaSection />}
                {introDone && <BetaCTA />}
            </main>
            <Footer />
            <Toaster theme="dark" position="bottom-center" />
        </div>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <Site />
        </ErrorBoundary>
    );
}
